import { Content } from '.generated/Feature.CW.model';
import { Field, ImageField, TextField, useSitecore } from '@sitecore-content-sdk/nextjs';
import ActionCardSection from 'helpers/ActionCardSection';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import { getPageThemeClass } from 'lib/utils/get-page-theme-class';
import { JSX } from 'react';
import { usePageData } from 'lib/utils/page-data-static-props/pageDataContext';
import { PageLanguages } from 'lib/utils/page-data-static-props/get-page-languages';

export type SpecialtyHeaderProps = Content.SpecialtyHeader;

const SpecialtyHeader = (props: SpecialtyHeaderProps): JSX.Element => {
  const { getDictionaryValue } = useDictionary();
  const { specialityActionCardsList } = props?.fields || {};
  const { page } = useSitecore();
  const { pageData } = usePageData<PageLanguages>();

  const contextWithSettings = page?.layout?.sitecore;
  const hideTranslationLink = contextWithSettings?.route?.fields
    ?.hideTranslationHeaderLink as Field<boolean>;

  const pageContextTitle = contextWithSettings?.route?.fields?.specialtyTitle as TextField;
  const pageContextHeroImage = contextWithSettings?.route?.fields
    ?.specialityHeroImage as ImageField;

  const componentTitle = props?.fields?.title?.value?.trim()
    ? props?.fields?.title
    : pageContextTitle;
  const componentHeroImage = props?.fields?.specialityHeroImage?.value?.src
    ? props?.fields?.specialityHeroImage
    : pageContextHeroImage;

  const templateID = contextWithSettings?.route?.templateId as string;
  const theme = getPageThemeClass(templateID);

  const language = page?.layout?.sitecore?.context?.language;
  const languages = pageData?.languages || [];
  const pagePath = page?.layout?.sitecore?.context?.itemPath || '';

  // Check if both en and es-ES exist
  const hasEN = languages.some((lang) => lang.isoCode === 'en');
  const hasES = languages.some((lang) => lang.isoCode === 'es-ES');

  // Determine alternate language link and text
  let alternateLanguageText = '';
  const alternateLanguageHref = pagePath;
  let locale = '';

  // this logic will be changed after url setting is done
  if (hasEN && hasES) {
    if (language === 'en') {
      alternateLanguageText = getDictionaryValue('LanguageCTAText');
      locale = `es-ES`;
    } else if (language === 'es-ES') {
      alternateLanguageText = getDictionaryValue('LanguageCTAText');
      locale = 'en';
    }
  }

  // Construct a new LinkField for the language switch link
  const customLanguageLink = {
    value: {
      text: alternateLanguageText,
      href: alternateLanguageHref,
      title: alternateLanguageText,
    },
  };

  return (
    <ComponentWrapper
      role="region"
      aria-label="Specialty Header for Orthopedics"
      className="!py-0"
      params={{
        Theme: theme,
      }}
    >
      <div className="bg-theme-bg lg:bg-transparent">
        <div className=" bg-theme-bg w-full ">
          <div className="lg:flex max-w-screen-xxl mx-auto justify-between lg:px-[80px] px-10 lg:pt-[64px] lg:pb-[58px] pt-[32px] gap-8">
            <h1 className="text-theme-text text-48 leading-58 font-bold font-centuryGothic pb-[19px] lg:pb-0">
              <PlainTextWrapper field={componentTitle} />
            </h1>

            {!hideTranslationLink?.value && (
              <div>
                {alternateLanguageText && locale && (
                  <LinkWrapper
                    field={customLanguageLink}
                    ctaVariant="tertiary"
                    className="pt-0 !text-left pb-4 w-fit text-theme-text-btn-text font-[600] !bg-transparent hover:!text-theme-text-btn-text-hover hover:underline"
                    ctaIcon="arrow-right"
                    aria-live="polite"
                    locale={locale}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <div className="w-full  hidden lg:block ">
          <ImageWrapper
            field={componentHeroImage}
            className="w-full max-h-[325px] object-cover object-center aspect-[288/65]"
            priority={true}
          />
        </div>
        {specialityActionCardsList && specialityActionCardsList.length > 0 && (
          <div
            className={
              'lg:max-w-screen-xxl m-auto relative lg:-top-[35px] theme-lightblue lg:bg-transparent  lg:p-[114px] px-10 lg:py-0 pt-[40px] pb-[32px]'
            }
          >
            <ActionCardSection
              actionCardsList={specialityActionCardsList ? specialityActionCardsList : []}
            />
          </div>
        )}
      </div>
    </ComponentWrapper>
  );
};

export default SpecialtyHeader;
